import React, { useEffect, useState, useCallback } from 'react';
import { SafeAreaView, View, StyleSheet, Text, ScrollView, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ModuleHeader, Card, PillButton, Avatar, PlatformIcon } from '@cubemeet/ui';

// redux thunks & helpers from core
import {
  getFriendRequests,
  getGroupInvitations,
  respondToFriendRequest,
  respondToGroupInvitation,
} from '@cubemeet/core/store/slices/friendsSlice';
import { clearUnreadCount, setBaseBusinessNotificationCount } from '@cubemeet/core/store/slices/notificationsSlice';

import { supabase } from '@cubemeet/core/services/supabase';
import { useI18n } from '../../i18n';
import type { AppDispatch, RootState } from '../../store';

const NotificationsModule: React.FC = () => {
  const { translations, language } = useI18n();
  const t = (key: string, vars?: Record<string, any>) => {
    // tiny translation accessor with safe fallback
    const parts = key.split('.');
    let cur: any = translations || {};
    for (const p of parts) {
      cur = cur?.[p];
      if (cur == null) return vars?.default || key;
    }
    if (typeof cur === 'string') {
      // Replace {{variable}} placeholders with actual values
      let result = cur;
      if (vars) {
        Object.keys(vars).forEach(varKey => {
          if (varKey !== 'default') {
            result = result.replace(new RegExp(`{{${varKey}}}`, 'g'), vars[varKey] || '');
          }
        });
      }
      return result;
    }
    return key;
  };

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth || {});
  const { friendRequests = [], groupInvitations = [], loading = false } = useSelector((state: RootState) => state.friends || {});

  const [enrichedFriendRequests, setEnrichedFriendRequests] = useState<any[]>([]);
  const [ledgerInvitations, setLedgerInvitations] = useState<any[]>([]);
  const [ledgerInvitationsLoading, setLedgerInvitationsLoading] = useState(false);
  const [todoInvitations, setTodoInvitations] = useState<any[]>([]);
  const [todoInvitationsLoading, setTodoInvitationsLoading] = useState(false);
  const [diningInvitations, setDiningInvitations] = useState<any[]>([]);
  const [diningInvitationsLoading, setDiningInvitationsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // 增强 friend requests，确保获取到 username
  useEffect(() => {
    const enrichFriendRequests = async () => {
      console.log('[Notifications] enrichFriendRequests called, friendRequests:', friendRequests);
      if (!friendRequests || friendRequests.length === 0) {
        setEnrichedFriendRequests([]);
        return;
      }
      
      const enriched = await Promise.all(
        friendRequests.map(async (req: any) => {
          console.log('[Notifications] Processing request:', req.id, 'sender_profile:', req.sender_profile);
          // 如果已经有 display_name 或 username，直接用
          if (req.sender_profile?.display_name || req.sender_profile?.username) {
            return req;
          }
          // 否则重新查询
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('display_name, avatar_url, username')
              .eq('id', req.sender_id)
              .single();
            console.log('[Notifications] Fetched profile for', req.sender_id, ':', profile);
            return {
              ...req,
              sender_profile: profile || req.sender_profile,
            };
          } catch (e) {
            console.error('[Notifications] Error fetching profile:', e);
            return req;
          }
        })
      );
      console.log('[Notifications] Enriched requests:', enriched);
      setEnrichedFriendRequests(enriched);
    };
    
    enrichFriendRequests();
  }, [friendRequests]);

  useEffect(() => {
    if (user) {
      loadData();
    }
    // clear unread badge when entering
    dispatch(clearUnreadCount());
    dispatch(setBaseBusinessNotificationCount(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      await Promise.all([
        dispatch(getFriendRequests(user.id)),
        dispatch(getGroupInvitations(user.id)),
        loadLedgerInvitations(),
        loadTodoInvitations(),
        loadDiningInvitations(),
      ]);
    } catch (err) {
      console.error('loadData error', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const formatDate = (date: string | Date) => new Date(date).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');

  // ledger invitations (uses supabase RPC like mobile)
  const loadLedgerInvitations = async () => {
    if (!user?.id) return;
    setLedgerInvitationsLoading(true);
    try {
      const { data, error } = await supabase
        .from('ledger_book_invitations')
        .select('*')
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('loadLedgerInvitations error', error);
        setLedgerInvitations([]);
      } else {
        const enriched = await Promise.all(
          (data || []).map(async (inv: any) => {
            const { data: inviterData } = await supabase
              .from('profiles')
              .select('display_name, avatar_url')
              .eq('id', inv.inviter_id)
              .single();
            const { data: ledgerData } = await supabase
              .from('personal_ledger_books')
              .select('name, description')
              .eq('id', inv.ledger_book_id)
              .single();
            return { ...inv, inviter: inviterData, ledger_book: ledgerData };
          })
        );
        setLedgerInvitations(enriched);
      }
    } catch (e) {
      console.error('loadLedgerInvitations exception', e);
      setLedgerInvitations([]);
    } finally {
      setLedgerInvitationsLoading(false);
    }
  };

  // todo invitations (fallback to supabase table)
  const loadTodoInvitations = async () => {
    if (!user?.id) return;
    setTodoInvitationsLoading(true);
    try {
      const { data, error } = await supabase
        .from('todo_invitations')
        .select('*')
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('loadTodoInvitations error', error);
        setTodoInvitations([]);
      } else {
        const enriched = await Promise.all(
          (data || []).map(async (inv: any) => {
            const { data: inviterData } = await supabase
              .from('profiles')
              .select('display_name, avatar_url')
              .eq('id', inv.inviter_id)
              .single();
            let category = null;
            if (inv.category_id) {
              const { data: cat } = await supabase
                .from('todo_categories')
                .select('name, description')
                .eq('id', inv.category_id)
                .single();
              category = cat;
            }
            return { ...inv, inviter: inviterData, todo_category: category };
          })
        );
        setTodoInvitations(enriched);
      }
    } catch (e) {
      console.error('loadTodoInvitations exception', e);
      setTodoInvitations([]);
    } finally {
      setTodoInvitationsLoading(false);
    }
  };

  // dining invitations: try to call API helper if available, otherwise leave empty
  const loadDiningInvitations = async () => {
    if (!user?.id) return;
    setDiningInvitationsLoading(true);
    try {
      // Try to import a DiningAPI if available on core. If not, skip.
      // This mirrors mobile behaviour but is tolerant on web if the module is absent.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      let DiningAPI: any = null;
      try {
        // dynamic require to avoid bundler errors if the module is missing
        // eslint-disable-next-line global-require
        DiningAPI = require('@cubemeet/core/services/diningAPI');
      } catch (err) {
        DiningAPI = null;
      }

      if (DiningAPI && DiningAPI.getUserInvitations) {
        const invitations = await DiningAPI.getUserInvitations();
        setDiningInvitations(invitations || []);
      } else {
        // graceful fallback: attempt to read a dining_invitations table if it exists
        try {
          const { data, error } = await supabase
            .from('dining_invitations')
            .select('*')
            .eq('invitee_id', user.id)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });
          if (!error) setDiningInvitations(data || []);
          else setDiningInvitations([]);
        } catch (e) {
          setDiningInvitations([]);
        }
      }
    } catch (e) {
      console.error('loadDiningInvitations exception', e);
      setDiningInvitations([]);
    } finally {
      setDiningInvitationsLoading(false);
    }
  };

  // handlers: friend/group use dispatched thunks; ledger/todo/dining make RPC/updates
  const handleFriendRequest = async (requestId: string, action: 'accept' | 'reject') => {
    try {
      await dispatch(respondToFriendRequest({ requestId, action })).unwrap();
      // optional: reload
      dispatch(getFriendRequests(user.id));
    } catch (e) {
      console.error('handleFriendRequest', e);
    }
  };

  const handleGroupInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      await dispatch(respondToGroupInvitation({ invitationId, action })).unwrap();
      dispatch(getGroupInvitations(user.id));
    } catch (e) {
      console.error('handleGroupInvitation', e);
    }
  };

  const handleLedgerInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      const newStatus = action === 'accept' ? 'accepted' : 'rejected';
      const { data: rpcResult, error: rpcError } = await supabase.rpc('update_ledger_invitation_status', {
        invitation_id: invitationId,
        new_status: newStatus,
      });
      if (rpcError) throw rpcError;
      await loadLedgerInvitations();
    } catch (e) {
      console.error('handleLedgerInvitation', e);
    }
  };

  const handleTodoInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      const newStatus = action === 'accept' ? 'accepted' : 'rejected';
      // try to update the invitation row directly
      const { error } = await supabase
        .from('todo_invitations')
        .update({ status: newStatus })
        .eq('id', invitationId);
      if (error) throw error;
      await loadTodoInvitations();
    } catch (e) {
      console.error('handleTodoInvitation', e);
    }
  };

  const handleDiningInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      // try DiningAPI.respondToInvitation if available, otherwise attempt to update dining_invitations
      let DiningAPI: any = null;
      try {
        // eslint-disable-next-line global-require
        DiningAPI = require('@cubemeet/core/services/diningAPI');
      } catch (err) {
        DiningAPI = null;
      }
      if (DiningAPI && DiningAPI.respondToInvitation) {
        await DiningAPI.respondToInvitation(invitationId, action === 'accept' ? 'accepted' : 'declined');
      } else {
        await supabase.from('dining_invitations').update({ status: action === 'accept' ? 'accepted' : 'declined' }).eq('id', invitationId);
      }
      await loadDiningInvitations();
    } catch (e) {
      console.error('handleDiningInvitation', e);
    }
  };

  const pendingRequests = enrichedFriendRequests.filter((req: any) => req.receiver_id === user?.id && req.status === 'pending');

  const isLoading = loading || ledgerInvitationsLoading || todoInvitationsLoading || diningInvitationsLoading;

  const renderNotifications = () => {
    if (isLoading && pendingRequests.length === 0 && groupInvitations.length === 0 && ledgerInvitations.length === 0 && todoInvitations.length === 0) {
      return (
        <View style={styles.loadingContainer}>
          <Text>{t('common.loading', { default: 'Loading...' })}</Text>
        </View>
      );
    }

    return (
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        {/* Friend requests */}
        {pendingRequests.length > 0 && (
          <View style={styles.notificationSection}>
            {pendingRequests.map((request: any) => {
              const senderName = request.sender_profile?.display_name || request.sender_profile?.username || 'Someone';
              return (
              <Card key={request.id} style={styles.itemCard}>
                <View style={styles.invitationInfo}>
                  <Avatar size="medium" avatarUrl={request.sender_profile?.avatar_url} displayName={senderName} />
                  <View style={styles.friendDetails}>
                    <Text style={styles.itemTitle}>{t('notifications.friendRequestFrom', { username: senderName, default: `${senderName} sent a friend request` })}</Text>
                    {request.message && <Text style={styles.messageText}>"{request.message}"</Text>}
                    <Text style={styles.dateText}>{formatDate(request.created_at)}</Text>
                  </View>
                </View>
                <View style={styles.actionButtons}>
                  <PillButton iconName="done" text={t('notifications.accept', { default: 'Accept' })} onPress={() => handleFriendRequest(request.id, 'accept')} style={styles.acceptButton} />
                  <PillButton iconName="close" text={t('notifications.decline', { default: 'Decline' })} variant="secondary" onPress={() => handleFriendRequest(request.id, 'reject')} style={styles.rejectButton} />
                </View>
              </Card>
            )})}
          </View>
        )}

        {/* Group invitations */}
        {groupInvitations.length > 0 && (
          <View style={styles.notificationSection}>
            {groupInvitations.map((invitation: any) => {
              const inviterName = invitation.inviter_profile?.display_name || invitation.inviter_profile?.username || 'Someone';
              const groupName = invitation.group?.name || 'a group';
              return (
              <Card key={invitation.id} style={styles.itemCard}>
                <View style={styles.invitationInfo}>
                  <Avatar size="medium" avatarUrl={invitation.inviter_profile?.avatar_url} displayName={inviterName} />
                  <View style={styles.friendDetails}>
                    <Text style={styles.itemTitle}>{t('notifications.groupInvitationFrom', { username: inviterName, groupName: groupName, default: `${inviterName} invited you to ${groupName}` })}</Text>
                    {invitation.group?.description && <Text style={styles.descriptionText}>{invitation.group.description}</Text>}
                    {invitation.message && <Text style={styles.messageText}>"{invitation.message}"</Text>}
                    <Text style={styles.roleText}>{t('notifications.role', { default: 'Role' })}: {invitation.role}</Text>
                  </View>
                </View>
                <View style={styles.actionButtons}>
                  <PillButton iconName="done" text={t('notifications.accept', { default: 'Accept' })} onPress={() => handleGroupInvitation(invitation.id, 'accept')} style={styles.acceptButton} />
                  <PillButton iconName="close" text={t('notifications.decline', { default: 'Decline' })} variant="secondary" onPress={() => handleGroupInvitation(invitation.id, 'reject')} style={styles.rejectButton} />
                </View>
              </Card>
            );})}
          </View>
        )}

        {/* Ledger invitations */}
        {ledgerInvitations.length > 0 && (
          <View style={styles.notificationSection}>
            {ledgerInvitations.map((invitation: any) => {
              const inviterName = invitation.inviter?.display_name || invitation.inviter?.username || 'Someone';
              const ledgerName = invitation.ledger_book?.name || 'a ledger';
              return (
              <Card key={invitation.id} style={styles.itemCard}>
                <View style={styles.invitationInfo}>
                  <Avatar size="medium" avatarUrl={invitation.inviter?.avatar_url} displayName={inviterName} />
                  <View style={styles.friendDetails}>
                    <Text style={styles.itemTitle}>{t('notifications.ledgerInvitationFrom', { username: inviterName, ledgerName: ledgerName, default: `${inviterName} invited you to ${ledgerName}` })}</Text>
                    {invitation.ledger_book?.description && <Text style={styles.descriptionText}>{invitation.ledger_book.description}</Text>}
                    {invitation.message && <Text style={styles.messageText}>"{invitation.message}"</Text>}
                    <Text style={styles.dateText}>{formatDate(invitation.created_at)}</Text>
                  </View>
                </View>
                <View style={styles.actionButtons}>
                  <PillButton iconName="done" text={t('notifications.accept', { default: 'Accept' })} onPress={() => handleLedgerInvitation(invitation.id, 'accept')} style={styles.acceptButton} />
                  <PillButton iconName="close" text={t('notifications.decline', { default: 'Decline' })} variant="secondary" onPress={() => handleLedgerInvitation(invitation.id, 'reject')} style={styles.rejectButton} />
                </View>
              </Card>
            );})}
          </View>
        )}

        {/* TODO invitations */}
        {todoInvitations.length > 0 && (
          <View style={styles.notificationSection}>
            {todoInvitations.map((invitation: any) => {
              const inviterName = invitation.inviter?.display_name || invitation.inviter?.username || 'Someone';
              const projectName = invitation.todo_category?.name || 'a project';
              return (
              <Card key={invitation.id} style={styles.itemCard}>
                <View style={styles.invitationInfo}>
                  <Avatar size="medium" avatarUrl={invitation.inviter?.avatar_url} displayName={inviterName} />
                  <View style={styles.friendDetails}>
                    <Text style={styles.itemTitle}>{t('notifications.todoInvitationFrom', { username: inviterName, projectName: projectName, default: `${inviterName} invited you to ${projectName}` })}</Text>
                    {invitation.todo_category?.description && <Text style={styles.descriptionText}>{invitation.todo_category.description}</Text>}
                    {invitation.message && <Text style={styles.messageText}>"{invitation.message}"</Text>}
                    <Text style={styles.dateText}>{formatDate(invitation.created_at)}</Text>
                  </View>
                </View>
                <View style={styles.actionButtons}>
                  <PillButton iconName="done" text={t('notifications.accept', { default: 'Accept' })} onPress={() => handleTodoInvitation(invitation.id, 'accept')} style={styles.acceptButton} />
                  <PillButton iconName="close" text={t('notifications.decline', { default: 'Decline' })} variant="secondary" onPress={() => handleTodoInvitation(invitation.id, 'reject')} style={styles.rejectButton} />
                </View>
              </Card>
            );})}
          </View>
        )}

        {/* Dining invitations */}
        {diningInvitations.length > 0 && (
          <View style={styles.notificationSection}>
            {diningInvitations.map((invitation: any) => (
              <Card key={invitation.id} style={styles.itemCard}>
                <View style={styles.invitationInfo}>
                  <Avatar size="medium" avatarUrl={invitation.inviter?.avatar_url} displayName={invitation.inviter?.display_name} />
                  <View style={styles.friendDetails}>
                    <Text style={styles.itemTitle}>{invitation.status === 'accepted' ? t('notifications.diningInvitations.acceptedTitle', { default: 'You joined a dining event' }) : t('notifications.diningInvitations.inviteTitle', { default: 'Dining invitation' })}</Text>
                    {invitation.dining_events?.restaurant_name && <Text style={styles.descriptionText}>{invitation.dining_events.restaurant_name}</Text>}
                    <Text style={styles.dateText}>{invitation.invited_at ? formatDate(invitation.invited_at) : formatDate(invitation.created_at)}</Text>
                  </View>
                </View>
                {invitation.status === 'pending' ? (
                  <View style={styles.actionButtons}>
                    <PillButton iconName="done" text={t('notifications.diningInvitations.actions.accept', { default: 'Accept' })} onPress={() => handleDiningInvitation(invitation.id, 'accept')} style={styles.acceptButton} />
                    <PillButton iconName="close" text={t('notifications.diningInvitations.actions.reject', { default: 'Reject' })} variant="secondary" onPress={() => handleDiningInvitation(invitation.id, 'reject')} style={styles.rejectButton} />
                  </View>
                ) : invitation.status === 'accepted' ? (
                  <View style={styles.actionButtons}>
                    <PillButton iconName="info" text={t('notifications.diningInvitations.actions.viewDetails', { default: 'View' })} onPress={() => {
                      try {
                        // store pending event id for the travel module to pick up (mirrors mobile behavior)
                        if (typeof window !== 'undefined') {
                          window.localStorage.setItem('pendingDiningEventId', invitation.event_id || invitation.id);
                          navigate('/travel');
                        }
                      } catch (e) {
                        console.error('navigate to dining', e);
                      }
                    }} style={styles.viewDetailButton} />
                  </View>
                ) : null}
              </Card>
            ))}
          </View>
        )}

        {/* Empty state */}
        {pendingRequests.length === 0 && groupInvitations.length === 0 && ledgerInvitations.length === 0 && todoInvitations.length === 0 && diningInvitations.length === 0 && !isLoading && (
          <Card style={styles.emptyCard}>
            <PlatformIcon name="notifications" size={48} color="#ccc" />
            <Text style={styles.emptyText}>{t('notifications.noNotifications', { default: 'No notifications' })}</Text>
            <Text style={styles.emptySubtext}>{t('notifications.noNotificationsSubtitle', { default: 'All caught up' })}</Text>
            <View style={{ marginTop: 12 }}>
              <PillButton iconName="refresh" text={t('common.refresh', { default: 'Refresh' })} onPress={onRefresh} />
            </View>
          </Card>
        )}
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.mainContainer}>
        <ModuleHeader moduleType="travel" title={t('notifications.title', { default: 'Notifications' })} subtitle={t('notifications.subtitle', { default: '' })} showMiniCube topInset={0} />
        {renderNotifications()}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f6f7f8' },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }
    }),
  },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 16, paddingVertical: 24, paddingBottom: 8 },
  bottomBarArea: { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingHorizontal: 24, paddingTop: 6, paddingBottom: 8, backgroundColor: '#FFFFFF', borderBottomLeftRadius: 28, borderBottomRightRadius: 28 },
  loadingContainer: { padding: 24, alignItems: 'center', justifyContent: 'center' },
  notificationSection: { marginBottom: 24 },
  itemCard: { marginBottom: 12, padding: 16 },
  invitationInfo: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  friendDetails: { flex: 1, marginLeft: 12 },
  itemTitle: { fontSize: 16, fontWeight: '600', color: '#333', marginBottom: 4 },
  descriptionText: { fontSize: 14, color: '#666', marginBottom: 4, lineHeight: 20 },
  messageText: { fontSize: 14, color: '#4A86F7', fontStyle: 'italic', marginBottom: 4 },
  roleText: { fontSize: 13, color: '#888', marginBottom: 4 },
  dateText: { fontSize: 12, color: '#999' },
  actionButtons: { flexDirection: 'row', gap: 8 },
  acceptButton: { flex: 1, backgroundColor: '#4A86F7' },
  rejectButton: { flex: 1, borderColor: '#ccc' },
  viewDetailButton: { flex: 1, backgroundColor: '#4A86F7' },
  emptyCard: { padding: 32, alignItems: 'center', marginTop: 32 },
  emptyText: { fontSize: 18, fontWeight: '600', color: '#666', marginTop: 16, textAlign: 'center' },
  emptySubtext: { fontSize: 14, color: '#999', marginTop: 8, textAlign: 'center', lineHeight: 20 },
});

export default NotificationsModule;
